import { useState } from 'react';
import { Button } from '../ui/button';

interface RollbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason?: string) => void;
  agentName: string;
  targetVersion: number;
  isRollingBack?: boolean;
}

export function RollbackModal({
  isOpen,
  onClose,
  onConfirm,
  agentName,
  targetVersion,
  isRollingBack = false,
}: RollbackModalProps) {
  const [reason, setReason] = useState('');

  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm(reason.trim() || undefined);
    setReason('');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className="relative z-50 w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rollback-modal-title"
      >
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
            <svg
              className="h-6 w-6 text-amber-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0019 16V8a1 1 0 00-1.6-.8l-5.334 4zM4.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0011 16V8a1 1 0 00-1.6-.8l-5.334 4z"
              />
            </svg>
          </div>
          <h3
            id="rollback-modal-title"
            className="text-lg font-semibold text-gray-900"
          >
            Rollback Agent
          </h3>
        </div>

        <p className="mb-4 text-sm text-gray-600">
          Are you sure you want to rollback{' '}
          <span className="font-semibold text-gray-900">"{agentName}"</span> to{' '}
          <span className="font-semibold text-gray-900">version {targetVersion}</span>?
          This will create a new version with the previous configuration.
        </p>

        <div className="mb-6">
          <label
            htmlFor="rollback-reason"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Reason (optional)
          </label>
          <textarea
            id="rollback-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why are you rolling back?"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            rows={3}
            maxLength={500}
          />
        </div>

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isRollingBack}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={isRollingBack}
          >
            {isRollingBack ? (
              <span className="flex items-center gap-2">
                <LoadingSpinner />
                Rolling back...
              </span>
            ) : (
              'Rollback'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <svg
      className="h-4 w-4 animate-spin"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

export default RollbackModal;
